import React from 'react';
import { Card, Button, Badge, Alert } from '../components/design-system';
import { ArrowRight, Zap, Shield, Globe, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const HomePage = () => {
  const { t } = useTranslation();
  
  return (
    <div className="p-6 space-y-6">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-8 md:p-12 text-white">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('pages.home.hero.title')}
          </h1>
          <p className="text-xl text-primary-50 mb-6">
            {t('pages.home.hero.subtitle')}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" size="lg">
              {t('pages.home.hero.getStarted')} <ArrowRight className="h-5 w-5" />
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
              {t('pages.home.hero.learnMore')}
            </Button>
          </div>
        </div>
      </div>

      {/* Alert Example */}
      <Alert variant="info" title={t('pages.home.alert.title')}>
        {t('pages.home.alert.message')}
      </Alert>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <FeatureCard
          icon={<Zap className="h-6 w-6 text-yellow-500" />}
          title={t('pages.home.features.performance.title')}
          description={t('pages.home.features.performance.description')}
          badge={t('pages.home.features.performance.badge')}
        />
        <FeatureCard
          icon={<Shield className="h-6 w-6 text-blue-500" />}
          title={t('pages.home.features.auth.title')}
          description={t('pages.home.features.auth.description')}
          badge={t('pages.home.features.auth.badge')}
        />
        <FeatureCard
          icon={<Globe className="h-6 w-6 text-green-500" />}
          title={t('pages.home.features.pwa.title')}
          description={t('pages.home.features.pwa.description')}
          badge={t('pages.home.features.pwa.badge')}
        />
        <FeatureCard
          icon={<TrendingUp className="h-6 w-6 text-purple-500" />}
          title={t('pages.home.features.scalable.title')}
          description={t('pages.home.features.scalable.description')}
          badge={t('pages.home.features.scalable.badge')}
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card title={t('pages.home.gettingStarted.title')} subtitle={t('pages.home.gettingStarted.subtitle')}>
            <div className="space-y-4">
              <Step
                number="1"
                title={t('pages.home.steps.step1.title')}
                description={t('pages.home.steps.step1.description')}
              />
              <Step
                number="2"
                title={t('pages.home.steps.step2.title')}
                description={t('pages.home.steps.step2.description')}
              />
              <Step
                number="3"
                title={t('pages.home.steps.step3.title')}
                description={t('pages.home.steps.step3.description')}
              />
              <Step
                number="4"
                title={t('pages.home.steps.step4.title')}
                description={t('pages.home.steps.step4.description')}
              />
            </div>
          </Card>

          <Card title={t('pages.home.activity.title')} subtitle={t('pages.home.activity.subtitle')}>
            <div className="space-y-3">
              <ActivityItem
                title="Design system components added"
                time="2 hours ago"
                status="success"
              />
              <ActivityItem
                title="Authentication flow implemented"
                time="5 hours ago"
                status="success"
              />
              <ActivityItem
                title="PWA configuration completed"
                time="1 day ago"
                status="success"
              />
            </div>
          </Card>
        </div>

        {/* Sidebar Content */}
        <div className="space-y-6">
          <Card title="Quick Stats">
            <div className="space-y-4">
              <StatItem label="Components" value="15+" color="text-primary-600" />
              <StatItem label="Pages" value="6" color="text-blue-600" />
              <StatItem label="Integrations" value="3" color="text-purple-600" />
            </div>
          </Card>

          <Card title="Resources">
            <div className="space-y-2">
              <ResourceLink title="Documentation" href="#" />
              <ResourceLink title="Account Service API" href="#" />
              <ResourceLink title="Component Library" href="#" />
              <ResourceLink title="Deployment Guide" href="#" />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, badge }) => (
  <Card padding="default">
    <div className="flex items-start justify-between mb-3">
      <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
      {badge && <Badge variant="primary" size="sm">{badge}</Badge>}
    </div>
    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </Card>
);

const Step = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 h-8 w-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
      {number}
    </div>
    <div>
      <h4 className="font-medium text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  </div>
);

const ActivityItem = ({ title, time, status }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{time}</p>
    </div>
    <Badge variant={status}>{status}</Badge>
  </div>
);

const StatItem = ({ label, value, color }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-2xl font-bold ${color}`}>{value}</span>
  </div>
);

const ResourceLink = ({ title, href }) => (
  <a
    href={href}
    className="block text-sm text-primary-600 hover:text-primary-700 hover:underline"
  >
    {title} →
  </a>
);
